import React, { useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { useTarget } from "../contexts/TargetContext";
import { useDialog } from "../contexts/DialogContext";
import PromptDialog from "./PromptDialog";
import {
  processTargetData,
  normalizeTargetName,
  deepEqual,
  getEquipmentSignature,
} from "../utils/dataProcessing";
// import { ImageMetadata } from "../types"; // Commented out as it's currently unused
import ClearIcon from "@mui/icons-material/Clear";
import { targetsApi } from "../utils/api";

// Move scanDir outside to avoid ES5 strict mode error
async function scanDir(
  handle: any,
  recursion: boolean,
  foundFiles: File[],
  path: string = ""
) {
  for await (const entry of handle.values()) {
    const entryPath = path ? path + "/" + entry.name : entry.name;
    console.log("scanDir entry:", {
      name: entry.name,
      kind: entry.kind,
      entryPath,
    });
    if (
      entry.kind === "file" &&
      (entry.name.toLowerCase() === "acquisitiondetails.json" ||
        entry.name.toLowerCase() === "imagemetadata.json" ||
        entry.name.toLowerCase() === "imagemetadata.json" || // fallback for both spellings
        entry.name.toLowerCase() === "acquisitiondetails.json")
    ) {
      const file = await entry.getFile();
      (file as any).entryPath = entryPath;
      foundFiles.push(file);
      console.log("Matched relevant file:", entryPath);
    } else if (entry.kind === "directory" && recursion) {
      await scanDir(entry, recursion, foundFiles, entryPath);
    }
  }
}

export const ImportDialog: React.FC = () => {
  const { importDialogOpen, setImportDialogOpen } = useDialog();

  const {
    acquisitionFile,
    metadataFile,
    loading,
    handleFileChange,
    // handleImport, // Commented out as it's currently unused, will be needed when implementing direct import from context
    setAcquisitionFile,
    setMetadataFile,
    targets,
    // handleImport: contextHandleImport, // Commented out as it's currently unused, will be needed when implementing batch imports
    // setSelectedTarget, // Commented out as it's currently unused, will be needed when implementing target selection after import
    fetchTargets,
  } = useTarget();

  // State for prompt dialog
  const [promptOpen, setPromptOpen] = React.useState(false);
  const [pendingImport, setPendingImport] = React.useState<null | {
    acquisitionData: any;
    metadataData: any;
  }>(null);
  const [targetName, setTargetName] = React.useState("");
  // const [folderFiles, setFolderFiles] = React.useState<FileList | null>(null); // Commented out as it's currently unused, will be needed for folder import feature
  const [recursion, setRecursion] = React.useState(true);
  // const folderInputRef = React.useRef<HTMLInputElement | null>(null); // Commented out as it's currently unused, will be needed for folder input reference
  const [relevantFiles, setRelevantFiles] = React.useState<File[]>([]);
  const [fsApiError, setFsApiError] = React.useState<string | null>(null);
  const [importSummary, setImportSummary] = React.useState<string | null>(null);
  const [importing, setImporting] = React.useState(false);
  const [overrideTargetName, setOverrideTargetName] = React.useState(false);
  const [overrideNameValue, setOverrideNameValue] = React.useState("");
  const [selectedFolders, setSelectedFolders] = React.useState<string[]>([]);

  // Helper function to reset file inputs
  const resetFileInputs = useCallback(() => {
    setAcquisitionFile(null);
    setMetadataFile(null);
    // Reset the actual file input elements by clearing their value
    const acqInput = document.getElementById(
      "acquisition-file-input"
    ) as HTMLInputElement;
    const metaInput = document.getElementById(
      "metadata-file-input"
    ) as HTMLInputElement;
    if (acqInput) acqInput.value = "";
    if (metaInput) metaInput.value = "";
  }, [setAcquisitionFile, setMetadataFile]);

  const handleAcquisitionFileChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    handleFileChange(event, "acquisition");
  };

  const handleMetadataFileChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    handleFileChange(event, "metadata");
  };

  // New direct import function
  const importWithData = async (acquisitionData: any, metadataData: any) => {
    // If override is enabled, set the target name
    if (overrideTargetName && overrideNameValue.trim()) {
      acquisitionData.TargetName = overrideNameValue.trim();
      if (Array.isArray(metadataData)) {
        metadataData.forEach((img: any) => {
          img.TargetName = overrideNameValue.trim();
        });
      } else if (metadataData && typeof metadataData === "object") {
        metadataData.TargetName = overrideNameValue.trim();
      }
    }
    // Build new target data
    const newTargetData = processTargetData(acquisitionData, metadataData);
    // --- Check for exact duplicate night ---
    try {
      // Fetch current targets using the API utility with auth headers
      const currentTargets = await targetsApi.getAll();

      // Ensure currentTargets is an array before using find
      if (!Array.isArray(currentTargets)) {
        throw new Error("Failed to fetch targets: Invalid response format");
      }

      const normName = normalizeTargetName(newTargetData.name);
      const newEquipSig = getEquipmentSignature(newTargetData.equipment);
      const existing = currentTargets.find(
        (t: any) =>
          normalizeTargetName(t.name) === normName &&
          t.equipment &&
          getEquipmentSignature(t.equipment) === newEquipSig
      );

      if (existing) {
        // Merge with existing target
        const mergedTarget = {
          ...existing,
          images: [...existing.images, ...newTargetData.images],
          nightMetadata: {
            ...existing.nightMetadata,
            ...newTargetData.nightMetadata,
          },
          totalExposureTime:
            (existing.totalExposureTime || 0) + newTargetData.totalExposureTime,
          filters: Array.from(
            new Set([
              ...(existing.filters || []),
              ...(newTargetData.filters || []),
            ])
          ),
        };
        // Update existing target
        await targetsApi.update(existing.id, mergedTarget);
        resetFileInputs();
        if (typeof fetchTargets === "function") {
          await fetchTargets();
        }
        setImportDialogOpen(false);
      } else {
        // Create new target as before
        await targetsApi.create(newTargetData);
        resetFileInputs();
        if (typeof fetchTargets === "function") {
          await fetchTargets();
        }
        setImportDialogOpen(false);
      }
    } catch (error) {
      console.error("Import error:", error);
      setImportSummary(
        `Import failed: ${
          error instanceof Error ? error.message : "Authentication required"
        }`
      );
      // Reset file inputs when an error occurs
      resetFileInputs();
    }
  };

  // Patch handleImport to check for missing TargetName
  const handleImportWithPrompt = async () => {
    if (!acquisitionFile || !metadataFile) return;

    // Clear any previous import summary
    setImportSummary(null);

    // Read files
    const acquisitionText = await acquisitionFile.text();
    const metadataText = await metadataFile.text();
    const acquisitionData = JSON.parse(acquisitionText);
    const metadataData = JSON.parse(metadataText);
    // If override is enabled, set the target name
    if (overrideTargetName && overrideNameValue.trim()) {
      acquisitionData.TargetName = overrideNameValue.trim();
      if (Array.isArray(metadataData)) {
        metadataData.forEach((img: any) => {
          img.TargetName = overrideNameValue.trim();
        });
      } else if (metadataData && typeof metadataData === "object") {
        metadataData.TargetName = overrideNameValue.trim();
      }
    }
    if (
      !acquisitionData.TargetName ||
      acquisitionData.TargetName.trim() === ""
    ) {
      setPendingImport({ acquisitionData, metadataData });
      setPromptOpen(true);
      setTargetName("");
      return;
    }
    // If TargetName exists, proceed as normal
    // --- Check for exact duplicate night ---
    const newTargetData = processTargetData(acquisitionData, metadataData);

    try {
      // Fetch current targets using the API utility with auth headers
      const currentTargets = await targetsApi.getAll();

      // Ensure currentTargets is an array before using find
      if (!Array.isArray(currentTargets)) {
        throw new Error("Failed to fetch targets: Invalid response format");
      }

      const normName = normalizeTargetName(newTargetData.name);
      const newEquipSig = getEquipmentSignature(newTargetData.equipment);
      const existing = currentTargets.find(
        (t: any) =>
          normalizeTargetName(t.name) === normName &&
          t.equipment &&
          getEquipmentSignature(t.equipment) === newEquipSig
      );
      if (existing) {
        // Check for exact duplicate night(s)
        let allNightsUnchanged = true;
        for (const night of Object.keys(newTargetData.nightMetadata)) {
          if (existing.nightMetadata[night]) {
            // Compare nightMetadata
            if (
              !deepEqual(
                existing.nightMetadata[night],
                newTargetData.nightMetadata[night]
              )
            ) {
              allNightsUnchanged = false;
              break;
            }
            // Compare images for this night
            const existingImages = existing.images.filter((img: any) => {
              const match = img.FilePath.match(/(\d{4}-\d{2}-\d{2})/);
              return match && match[1] === night;
            });
            const newImages = newTargetData.images.filter((img: any) => {
              const match = img.FilePath.match(/(\d{4}-\d{2}-\d{2})/);
              return match && match[1] === night;
            });
            if (!deepEqual(existingImages, newImages)) {
              allNightsUnchanged = false;
              break;
            }
          } else {
            allNightsUnchanged = false;
            break;
          }
        }
        if (allNightsUnchanged) {
          setImportSummary(
            `Target "${existing.name}" already exists with the same equipment configuration and nights. No changes were made.`
          );
          // Reset file inputs when showing error message
          resetFileInputs();
          return;
        }
      }
      importWithData(acquisitionData, metadataData);
    } catch (error) {
      console.error("Import error:", error);
      setImportSummary(
        `Import failed: ${
          error instanceof Error ? error.message : "Authentication required"
        }`
      );
      // Reset file inputs when an error occurs
      resetFileInputs();
    }
  };

  // When user confirms prompt
  const handlePromptConfirm = () => {
    if (pendingImport && targetName.trim()) {
      // Use the shared normalizeTargetName utility
      const enteredNorm = normalizeTargetName(targetName.trim());
      // Try to find an existing target with the same normalized name
      const existing = targets.find(
        (t) =>
          normalizeTargetName(t.name) === enteredNorm &&
          t.equipment &&
          getEquipmentSignature(t.equipment) ===
            getEquipmentSignature(
              processTargetData(
                pendingImport.acquisitionData,
                pendingImport.metadataData
              ).equipment
            )
      );
      if (existing) {
        // Use the existing target's name (original casing/spacing) everywhere
        pendingImport.acquisitionData.TargetName = existing.name;
      } else {
        pendingImport.acquisitionData.TargetName = targetName.trim();
      }
      setPromptOpen(false);
      setPendingImport(null);
      setTargetName("");
      // Call importWithData directly with patched data
      importWithData(pendingImport.acquisitionData, pendingImport.metadataData);
    } else {
      setPromptOpen(false);
      setPendingImport(null);
      setTargetName("");
    }
  };

  // File System Access API folder picker
  const handleFsApiFolderPick = async () => {
    setFsApiError(null);
    setRelevantFiles([]);
    console.log("Folder picker triggered");
    if (!("showDirectoryPicker" in window)) {
      setFsApiError(
        "Your browser does not support efficient folder selection. Please use Chrome or Edge for the best experience."
      );
      console.warn("File System Access API not available");
      return;
    }
    try {
      const dirHandle = await (window as any).showDirectoryPicker();
      const foundFiles: File[] = [];
      await scanDir(dirHandle, recursion, foundFiles, "");
      setRelevantFiles(foundFiles);
      console.log("Relevant files after folder pick:", foundFiles);
      if (foundFiles.length === 0) {
        setFsApiError("No relevant files found in the selected folder.");
      }
    } catch (err: any) {
      setFsApiError("Folder selection was cancelled or failed.");
      console.error("Folder selection error:", err);
    }
  };

  // Helper: Group files by parent directory
  function groupFilesByFolder(files: File[]) {
    const map = new Map<string, { acquisition?: File; metadata?: File }>();
    for (const file of files) {
      let folder = "";
      // Prefer entryPath if available (from scanDir)
      if ((file as any).entryPath) {
        const entryPath = (file as any).entryPath;
        folder = entryPath.substring(0, entryPath.lastIndexOf("/"));
      } else if ((file as any).webkitRelativePath) {
        const rel = (file as any).webkitRelativePath;
        folder = rel.substring(0, rel.lastIndexOf("/"));
      } else if ((file as any).path) {
        folder = (file as any).path.substring(
          0,
          (file as any).path.lastIndexOf("/")
        );
      } else {
        folder = (file as any).parent || "";
      }
      if (!map.has(folder)) map.set(folder, {});
      if (file.name.toLowerCase() === "acquisitiondetails.json")
        map.get(folder)!.acquisition = file;
      if (file.name.toLowerCase() === "imagemetadata.json")
        map.get(folder)!.metadata = file;
    }
    return map;
  }

  // Helper: Get folder map and list of valid folders
  const folderMap = React.useMemo(
    () => groupFilesByFolder(relevantFiles),
    [relevantFiles]
  );
  const validFolders = React.useMemo(
    () =>
      Array.from(folderMap.entries())
        .filter(([_, files]) => files.acquisition && files.metadata)
        .map(([folder]) => folder),
    [folderMap]
  );

  // Effect: When relevantFiles changes, select all by default
  React.useEffect(() => {
    if (validFolders.length > 0) {
      setSelectedFolders(validFolders);
    } else {
      setSelectedFolders([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [relevantFiles]);

  // Select all/deselect all handlers
  const allSelected =
    selectedFolders.length === validFolders.length && validFolders.length > 0;
  const handleSelectAll = (checked: boolean) => {
    setSelectedFolders(checked ? validFolders : []);
  };
  const handleFolderCheckbox = (folder: string) => {
    setSelectedFolders((prev) =>
      prev.includes(folder)
        ? prev.filter((f) => f !== folder)
        : [...prev, folder]
    );
  };

  // Define the type for import results
  type ImportResult = {
    imported: number;
    skipped: number;
    error: string | null;
    createTarget?: any;
    updateTarget?: { id: string; data: any };
  };

  // Mass import handler
  const handleMassImport = async () => {
    console.log("Mass import button clicked");
    setImporting(true);
    setImportSummary(null);
    const folderMap = groupFilesByFolder(relevantFiles);
    let errors: string[] = [];
    let attempted = 0;
    // let skippedUnchanged = 0; // Commented out as it's currently unused, will be needed for tracking unchanged imports
    // Only use selected folders
    const selectedFolderEntries = Array.from(folderMap.entries()).filter(
      ([folder]) => selectedFolders.includes(folder)
    );
    if (selectedFolderEntries.length === 0) {
      alert(
        "No folders selected for import. Please select at least one folder containing both acquisitionDetails.json and ImageMetadata.json."
      );
      setImporting(false);
      setImportSummary("No folders selected for import. Nothing was imported.");
      // Reset file inputs when showing error message
      resetFileInputs();
      return;
    }
    // Step 1: Parse all pairs and group by normalized TargetName AND equipment signature
    const targetGroups: Record<
      string,
      { acquisitionList: any[]; images: any[] }
    > = {};
    for (const [folder, files] of selectedFolderEntries) {
      if (files.acquisition && files.metadata) {
        attempted++;
        try {
          const acquisitionText = await files.acquisition.text();
          const metadataText = await files.metadata.text();
          const acquisitionData = JSON.parse(acquisitionText);
          const metadataData = JSON.parse(metadataText);
          // If override is enabled, set the target name
          if (overrideTargetName && overrideNameValue.trim()) {
            acquisitionData.TargetName = overrideNameValue.trim();
            if (Array.isArray(metadataData)) {
              metadataData.forEach((img: any) => {
                img.TargetName = overrideNameValue.trim();
              });
            } else if (metadataData && typeof metadataData === "object") {
              metadataData.TargetName = overrideNameValue.trim();
            }
          }
          const normName = normalizeTargetName(
            acquisitionData.TargetName || "ImportedTarget"
          );
          const equipSig = getEquipmentSignature({
            telescopeName: acquisitionData.TelescopeName,
            focalLength: acquisitionData.FocalLength,
            focalRatio: acquisitionData.FocalRatio,
            cameraName: acquisitionData.CameraName,
            pixelSize: acquisitionData.PixelSize,
            bitDepth: acquisitionData.BitDepth,
          });
          const groupKey = `${normName}__${equipSig}`;
          if (!targetGroups[groupKey]) {
            targetGroups[groupKey] = { acquisitionList: [], images: [] };
          }
          targetGroups[groupKey].acquisitionList.push(acquisitionData);
          if (Array.isArray(metadataData)) {
            targetGroups[groupKey].images.push(...metadataData);
          } else if (metadataData) {
            targetGroups[groupKey].images.push(metadataData);
          }
        } catch (err: any) {
          console.error(`Error parsing in folder '${folder}':`, err);
          errors.push(
            `Error parsing in folder '${folder}': ${err.message || err}`
          );
        }
      }
    }

    // Step 2: For each group, merge and import
    const importPromises = Object.keys(targetGroups).map(async (groupKey) => {
      const normName = groupKey.split("__")[0];
      try {
        const group = targetGroups[groupKey];
        // Use the first acquisitionDetails as the base
        const baseAcq = group.acquisitionList[0];
        // Merge all images
        const allImages = group.images;
        // Use processTargetData to build the merged target
        const newTargetData = processTargetData(baseAcq, allImages);

        try {
          // Fetch current targets using the API utility with auth headers
          const currentTargets = await targetsApi.getAll();

          // Ensure currentTargets is an array before using find
          if (!Array.isArray(currentTargets)) {
            throw new Error("Failed to fetch targets: Invalid response format");
          }

          const newEquipSig = getEquipmentSignature(newTargetData.equipment);
          const existing = currentTargets.find(
            (t: any) =>
              normalizeTargetName(t.name) === normName &&
              t.equipment &&
              getEquipmentSignature(t.equipment) === newEquipSig
          );
          let allNightsUnchanged = true;
          if (existing) {
            for (const night of Object.keys(newTargetData.nightMetadata)) {
              if (existing.nightMetadata[night]) {
                if (
                  !deepEqual(
                    existing.nightMetadata[night],
                    newTargetData.nightMetadata[night]
                  )
                ) {
                  allNightsUnchanged = false;
                  break;
                }
                const existingImages = existing.images.filter((img: any) => {
                  const match = img.FilePath.match(/(\d{4}-\d{2}-\d{2})/);
                  return match && match[1] === night;
                });
                const newImages = newTargetData.images.filter((img: any) => {
                  const match = img.FilePath.match(/(\d{4}-\d{2}-\d{2})/);
                  return match && match[1] === night;
                });
                if (!deepEqual(existingImages, newImages)) {
                  allNightsUnchanged = false;
                  break;
                }
              } else {
                allNightsUnchanged = false;
                break;
              }
            }
          } else {
            allNightsUnchanged = false;
          }
          if (allNightsUnchanged) {
            // skippedUnchanged++;
            return { imported: 0, skipped: 1, error: null };
          }

          // Instead of immediately importing, return data for batch operations
          if (existing) {
            // Merge with existing target
            const mergedTarget = {
              ...existing,
              images: [...existing.images, ...newTargetData.images],
              nightMetadata: {
                ...existing.nightMetadata,
                ...newTargetData.nightMetadata,
              },
              totalExposureTime:
                (existing.totalExposureTime || 0) +
                newTargetData.totalExposureTime,
              filters: Array.from(
                new Set([
                  ...(existing.filters || []),
                  ...(newTargetData.filters || []),
                ])
              ),
            };
            return {
              imported: 1,
              skipped: 0,
              error: null,
              updateTarget: { id: existing.id, data: mergedTarget },
            };
          } else {
            // Create new target
            return {
              imported: 1,
              skipped: 0,
              error: null,
              createTarget: newTargetData,
            };
          }
        } catch (err: any) {
          console.error(`Error fetching targets for '${normName}':`, err);
          return {
            imported: 0,
            skipped: 0,
            error: `Error fetching targets for '${normName}': ${
              err.message || err
            }`,
          };
        }
      } catch (err: any) {
        console.error(`Error importing target '${normName}':`, err);
        return {
          imported: 0,
          skipped: 0,
          error: `Error importing target '${normName}': ${err.message || err}`,
        };
      }
    });

    // Wait for all imports to complete in parallel
    const results = (await Promise.all(importPromises)) as ImportResult[];

    // Aggregate results
    const importStats = results.reduce(
      (acc, result) => {
        if (result.imported) acc.imported += result.imported;
        if (result.skipped) acc.skipped += result.skipped;
        if (result.error) acc.errors.push(result.error);
        return acc;
      },
      { imported: 0, skipped: 0, errors: [] as string[] }
    );

    // Process all import results and prepare batch operations
    const createOperations: any[] = [];
    const updateOperations: Array<{ id: string; data: any }> = [];

    for (const result of results) {
      if (result.createTarget) {
        createOperations.push(result.createTarget);
      }
      if (result.updateTarget) {
        updateOperations.push(result.updateTarget);
      }
    }

    // Execute batch operations if any
    if (createOperations.length > 0) {
      // Use individual creation instead of batch import to avoid payload size issues
      await Promise.all(
        createOperations.map((target) => targetsApi.create(target))
      );
    }

    if (updateOperations.length > 0) {
      await targetsApi.batchUpdate(updateOperations);
    }

    setImporting(false);
    if (attempted === 0) {
      setImportSummary("No folders selected for import. Nothing was imported.");
    } else {
      setImportSummary(
        `Imported ${importStats.imported} targets.${
          importStats.skipped
            ? ` ${importStats.skipped} target(s) were not imported because they already exist with the same data.`
            : ""
        }${
          importStats.errors.length
            ? " Errors: " + importStats.errors.join("; ")
            : ""
        }`
      );
    }

    // Reset file inputs after import
    resetFileInputs();
    // Optionally, refresh targets
    if (typeof fetchTargets === "function") await fetchTargets();
    setImportDialogOpen(false);
  };

  // Add effect to log relevantFiles when they change
  React.useEffect(() => {
    if (relevantFiles.length > 0) {
      console.log("Rendering import button, relevantFiles:", relevantFiles);
    }
  }, [relevantFiles]);

  React.useEffect(() => {
    if (importDialogOpen) {
      setImportSummary(null);
      setFsApiError(null);
      setRelevantFiles([]);
    } else {
      setImporting(false);
      // Reset file inputs when dialog closes
      resetFileInputs();
    }
  }, [importDialogOpen, resetFileInputs]);

  return (
    <>
      <Dialog
        open={importDialogOpen}
        onClose={() => {
          setImportDialogOpen(false);
          resetFileInputs();
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            backgroundColor: "#181818",
            boxShadow: "0 8px 32px rgba(0,0,0,0.8)",
            minWidth: 500,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            fontSize: 28,
            color: "#fff",
            background: "#181818",
            pb: 2,
            pl: 3,
            pr: 6,
            pt: 3,
            position: "relative",
          }}
        >
          Import New Target
        </DialogTitle>
        <DialogContent
          sx={{
            background: "#181818",
            px: 3,
            pt: 2,
            pb: 1,
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {/* Override Target Name Option - label and box grouped */}
            <Box>
              <label
                htmlFor="override-target-name-checkbox"
                style={{
                  color: "#aaa",
                  fontSize: 14,
                  marginBottom: 2,
                  display: "block",
                }}
              >
                Override Target Name
              </label>
              <Box
                sx={{
                  border: "1.5px solid #333",
                  borderRadius: 2,
                  background: "#202020",
                  p: 1.3,
                  maxWidth: 350,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <input
                    type="checkbox"
                    id="override-target-name-checkbox"
                    checked={overrideTargetName}
                    onChange={(e) => setOverrideTargetName(e.target.checked)}
                    style={{ accentColor: "#ff6b35", width: 14, height: 14 }}
                  />
                  <TextField
                    size="small"
                    variant="outlined"
                    placeholder="Enter new target name"
                    value={overrideNameValue}
                    onChange={(e) => setOverrideNameValue(e.target.value)}
                    disabled={!overrideTargetName}
                    sx={{
                      minWidth: 100,
                      background: "#232323",
                      input: { color: "#fff", fontSize: 14 },
                    }}
                    inputProps={{ style: { color: "#fff", fontSize: 14 } }}
                  />
                </Box>
              </Box>
            </Box>
            <Box>
              <label
                style={{
                  color: "#aaa",
                  fontSize: 14,
                  marginBottom: 2,
                  display: "block",
                }}
              >
                Acquisition Details
              </label>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  border: "1.5px solid #333",
                  borderRadius: 2,
                  background: "#232323",
                  p: "4px 8px",
                  maxWidth: 350,
                }}
              >
                <input
                  id="acquisition-file-input"
                  type="file"
                  accept=".json,application/json"
                  style={{ display: "none" }}
                  onChange={handleAcquisitionFileChange}
                />
                <label htmlFor="acquisition-file-input">
                  <Button
                    variant="contained"
                    component="span"
                    sx={{
                      background: "#232323",
                      color: "#ff6b35",
                      fontWeight: 600,
                      borderRadius: 2,
                      px: 1.5,
                      py: 0.7,
                      fontSize: 15,
                      minWidth: 0,
                      boxShadow: "none",
                      "&:hover": {
                        background: "#333",
                        color: "#fff",
                      },
                    }}
                  >
                    Choose File
                  </Button>
                </label>
                <span
                  style={{
                    color: "#fff",
                    fontSize: 14,
                    lineHeight: 1.5,
                    wordBreak: "break-all",
                    verticalAlign: "middle",
                    flex: 1,
                  }}
                >
                  {acquisitionFile ? acquisitionFile.name : "No file chosen"}
                </span>
                {acquisitionFile && (
                  <Button
                    onClick={() => setAcquisitionFile(null)}
                    size="small"
                    sx={{ minWidth: 0, p: 0.5, color: "#ff6b35" }}
                    aria-label="Clear file"
                  >
                    <ClearIcon fontSize="small" />
                  </Button>
                )}
              </Box>
            </Box>
            <Box>
              <label
                style={{
                  color: "#aaa",
                  fontSize: 14,
                  marginBottom: 2,
                  display: "block",
                }}
              >
                Image Metadata
              </label>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  border: "1.5px solid #333",
                  borderRadius: 2,
                  background: "#232323",
                  p: "4px 8px",
                  maxWidth: 350,
                }}
              >
                <input
                  id="metadata-file-input"
                  type="file"
                  accept=".json,application/json"
                  style={{ display: "none" }}
                  onChange={handleMetadataFileChange}
                />
                <label htmlFor="metadata-file-input">
                  <Button
                    variant="contained"
                    component="span"
                    sx={{
                      background: "#232323",
                      color: "#ff6b35",
                      fontWeight: 600,
                      borderRadius: 2,
                      px: 1.5,
                      py: 0.7,
                      fontSize: 15,
                      minWidth: 0,
                      boxShadow: "none",
                      "&:hover": {
                        background: "#333",
                        color: "#fff",
                      },
                    }}
                  >
                    Choose File
                  </Button>
                </label>
                <span
                  style={{
                    color: "#fff",
                    fontSize: 14,
                    lineHeight: 1.5,
                    wordBreak: "break-all",
                    verticalAlign: "middle",
                    flex: 1,
                  }}
                >
                  {metadataFile ? metadataFile.name : "No file chosen"}
                </span>
                {metadataFile && (
                  <Button
                    onClick={() => setMetadataFile(null)}
                    size="small"
                    sx={{ minWidth: 0, p: 0.5, color: "#ff6b35" }}
                    aria-label="Clear file"
                  >
                    <ClearIcon fontSize="small" />
                  </Button>
                )}
              </Box>
            </Box>

            {/* Display import summary/error message for manual import */}
            {importSummary && relevantFiles.length === 0 && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  border: "1px solid #ff6b35",
                  borderRadius: 2,
                  background: "rgba(255,107,53,0.1)",
                }}
              >
                <div style={{ color: "#ff6b35", fontSize: 14 }}>
                  {importSummary}
                </div>
              </Box>
            )}

            <Box
              sx={{
                mt: 2,
                p: 2,
                border: "1px solid #333",
                borderRadius: 2,
                background: "#222",
              }}
            >
              <label
                style={{
                  color: "#ff6b35",
                  fontWeight: 600,
                  fontSize: 16,
                  display: "block",
                  marginBottom: 4,
                }}
              >
                Import By Folder
              </label>
              <Button
                variant="contained"
                sx={{ color: "#fff", background: "#ff6b35", mb: 1, mr: 2 }}
                onClick={handleFsApiFolderPick}
              >
                Select Folder
              </Button>
              <label
                style={{
                  color: "#aaa",
                  fontSize: 14,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginTop: 8,
                }}
              >
                <input
                  type="checkbox"
                  checked={recursion}
                  onChange={(e) => setRecursion(e.target.checked)}
                />
                Include Subfolders
              </label>
              {fsApiError && (
                <div style={{ color: "#ff6b35", fontSize: 13, marginTop: 8 }}>
                  {fsApiError}
                </div>
              )}
              {relevantFiles.length > 0 && (
                <div style={{ color: "#aaa", fontSize: 13, marginTop: 8 }}>
                  {relevantFiles.length} relevant files found
                  (acquisitionDetails.json, ImageMetadata.json)
                  {importSummary && (
                    <div
                      style={{ color: "#ff6b35", fontSize: 13, marginTop: 8 }}
                    >
                      {importSummary}
                    </div>
                  )}
                  <div
                    style={{
                      color: "#fff",
                      fontSize: 12,
                      marginTop: 10,
                      maxHeight: 220,
                      overflowY: "auto",
                      background: "#181818",
                      border: "1px solid #333",
                      borderRadius: 4,
                      padding: 8,
                    }}
                  >
                    <strong>Folders with both files (select to import):</strong>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 1,
                        mt: 1,
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={allSelected}
                            indeterminate={
                              selectedFolders.length > 0 &&
                              selectedFolders.length < validFolders.length
                            }
                            onChange={(e) => handleSelectAll(e.target.checked)}
                            sx={{ color: "#ff6b35" }}
                          />
                        }
                        label={allSelected ? "Deselect All" : "Select All"}
                        sx={{ color: "#fff", fontSize: 13, ml: 0 }}
                      />
                    </Box>
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                      {validFolders.length === 0 && (
                        <li style={{ color: "#ff6b35" }}>
                          No folders contained both acquisitionDetails.json and
                          ImageMetadata.json.
                        </li>
                      )}
                      {validFolders.map((folder) => {
                        const files = folderMap.get(folder);
                        return (
                          <li
                            key={folder}
                            style={{ wordBreak: "break-all", marginBottom: 6 }}
                          >
                            <Checkbox
                              checked={selectedFolders.includes(folder)}
                              onChange={() => handleFolderCheckbox(folder)}
                              sx={{
                                color: "#ff6b35",
                                p: 0.5,
                                verticalAlign: "middle",
                              }}
                            />
                            <span
                              style={{
                                color: "#fff",
                                fontSize: 13,
                                marginLeft: 4,
                              }}
                            >
                              {folder || "/"}
                            </span>
                            <span
                              style={{
                                color: "#aaa",
                                fontSize: 12,
                                marginLeft: 8,
                              }}
                            >
                              [{files?.acquisition?.name || "-"},{" "}
                              {files?.metadata?.name || "-"}]
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            background: "#181818",
            px: 3,
            pb: 3,
            pt: 2,
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Button
            onClick={() => {
              setImportDialogOpen(false);
              resetFileInputs();
            }}
            sx={{
              color: "#ff6b35",
              fontWeight: 600,
              fontSize: 16,
              letterSpacing: 1,
              px: 2,
              textTransform: "none",
              "&:hover": {
                background: "rgba(255,107,53,0.08)",
                color: "#fff",
              },
            }}
          >
            CANCEL
          </Button>
          <Button
            onClick={
              relevantFiles.length > 0
                ? handleMassImport
                : handleImportWithPrompt
            }
            disabled={
              (relevantFiles.length === 0 &&
                (!acquisitionFile || !metadataFile || loading)) ||
              importing
            }
            startIcon={
              loading || importing ? <CircularProgress size={20} /> : null
            }
            sx={{
              minWidth: 180,
              fontWeight: 600,
              fontSize: 16,
              background:
                (relevantFiles.length === 0 &&
                  (!acquisitionFile || !metadataFile || loading)) ||
                importing
                  ? "#222"
                  : "#ff6b35",
              color:
                (relevantFiles.length === 0 &&
                  (!acquisitionFile || !metadataFile || loading)) ||
                importing
                  ? "#555"
                  : "#fff",
              borderRadius: 2,
              textTransform: "none",
              boxShadow: "none",
              pointerEvents:
                (relevantFiles.length === 0 &&
                  (!acquisitionFile || !metadataFile || loading)) ||
                importing
                  ? "none"
                  : "auto",
              "&:hover": {
                background:
                  (relevantFiles.length === 0 &&
                    (!acquisitionFile || !metadataFile || loading)) ||
                  importing
                    ? "#222"
                    : "#ff8a65",
                color:
                  (relevantFiles.length === 0 &&
                    (!acquisitionFile || !metadataFile || loading)) ||
                  importing
                    ? "#555"
                    : "#fff",
              },
              "&.Mui-disabled": {
                background: "#222",
                color: "#555",
              },
            }}
          >
            {relevantFiles.length > 0
              ? importing
                ? "Importing..."
                : "Import All Found Targets"
              : loading
              ? "Importing..."
              : "Import"}
          </Button>
        </DialogActions>
      </Dialog>
      <PromptDialog
        open={promptOpen}
        label="Target Name"
        value={targetName}
        onChange={setTargetName}
        onCancel={() => {
          setPromptOpen(false);
          setPendingImport(null);
          setTargetName("");
        }}
        onConfirm={handlePromptConfirm}
        title="Enter Target Name"
        confirmText="OK"
        cancelText="Cancel"
        helperText="Please enter the target name for these frames."
      />
    </>
  );
};

export default ImportDialog;
